import { useEffect, useState } from "react";
import { BaseLayoutNoMenu } from "./layouts/BaseLayout";
import { WizardProgress } from "./fragments/WizardProgress";
import {
  TbBuildingBank,
  TbBuildingCommunity,
  TbCashBanknote,
  TbLocationBolt,
} from "react-icons/tb";
import { RxRocket } from "react-icons/rx";
import { StepSelectType } from "./fragments/StepSelectType";

import { useSearchParams } from "react-router-dom";
import { StepClaimOrganization } from "./fragments/StepClaimOrganization";
import {
  nextStepByType,
  SETUP_KEYS,
  SYSTEM_USER_TYPES,
  UserType,
} from "./types";
import { get } from "lodash";
import { StepPreferences } from "./fragments/StepPreferences";
import { StepConfirmation } from "./fragments/StepConfirmation";
import { useAuth } from "../../context/AuthContext";
import { useProjectDocumentTitle } from "../../utils/document";
import { areTermsAndConditionsUpToDate } from "../../utils/tos";

const userAccountTypes = [
  {
    type: "FOUNDER",
    label: "Founder",
    description:
      "You're building a venture and looking to connect with investors, track similar organisations, and grow your understanding of the market.",
    icon: <RxRocket />,
  },
  {
    type: "INVESTOR",
    label: "Investor",
    description:
      "You fund startups or projects and need deal flow, portfolio tracking, company deepdives, and live funding activity.",
    icon: <TbCashBanknote />,
  },
  {
    type: "DFI/GOVT",
    label: "DFI or Government",
    description:
      "You represent a development finance institution or public body supporting innovation and economic growth.",
    icon: <TbBuildingBank />,
  },
  {
    type: "ACCELERATOR",
    label: "Accelerator or Hub",
    description:
      "You run programmes that mentor, fund and support early-stage ventures and are looking to source companies for your next batch, help your portfolio find next-round investors, and build market intelligence with research.",
    icon: <TbLocationBolt />,
  },
  {
    type: "ACADEMIA",
    label: "Research or Academia",
    description:
      "You study markets, ecosystems, or innovation trends and need the latest startup trends, ready-made visuals, and a trusted data partner for your analysis.",
    icon: <TbBuildingCommunity />,
  },
  {
    type: "GENERIC",
    label: "Not sure yet",
    description:
      "I am not sure how I am going to use the platform right now.",
    standalone: true,
  },
];

const determineInitialStep = (user: any): string => {
  // Check if all basic account info is complete
  const hasBasicInfo =
    user?.country &&
    user?.role &&
    user?.organization_raw &&
    user?.organization_raw_type &&
    user?.system_user_type &&
    user?.terms_conditions_accepted &&
    areTermsAndConditionsUpToDate(user?.terms_conditions_accepted_at);

  // If basic info incomplete, start at ACCOUNT
  if (!hasBasicInfo) return SETUP_KEYS.ACCOUNT;

  // If user is FOUNDER, check if they need to claim
  if (user?.system_user_type === SYSTEM_USER_TYPES.FOUNDER) {
    const hasValidClaim = user?.claims?.some(
      (c: any) => c.status === 0 || c.status === 1,
    );
    if (!hasValidClaim) return SETUP_KEYS.CLAIM;
  }

  // If non-FOUNDER, check if preferences are set
  if (user?.system_user_type !== SYSTEM_USER_TYPES.FOUNDER) {
    const hasPreferences =
      user?.metadata?.preferred_countries?.length > 0 ||
      user?.metadata?.preferred_agbase_categories?.length > 0 ||
      user?.metadata?.preferred_agbase_value_chains?.length > 0;
    if (!hasPreferences) return SETUP_KEYS.PREFERENCES;
  }

  // Everything complete, go to confirmation
  return SETUP_KEYS.CONFIRMATION;
};

export const UserWizardContainer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { session, loading } = useAuth();
  const [userType, setUserType] = useState<UserType>(
    get(
      session,
      "user.system_user_type",
      SYSTEM_USER_TYPES.DEFAULT,
    ) as UserType,
  );

  const stepTitles: Record<string, string> = {
    [SETUP_KEYS.ACCOUNT]: "How will you use the platform?",
    [SETUP_KEYS.CLAIM]: "Claim your organisation",
    [SETUP_KEYS.PREFERENCES]: "Set your preferences",
    [SETUP_KEYS.CONFIRMATION]: "You're all set",
  };
  const currentStep = searchParams.get("step") || SETUP_KEYS.ACCOUNT;
  useProjectDocumentTitle(stepTitles[currentStep] ?? "Get started");
  useEffect(() => {
    // Only set initial step if no step is set AND user data is available
    if (!searchParams.get("step") && session?.user) {
      const initialStep = determineInitialStep(session.user);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev); // ✅ clone, don't mutate
        newParams.set("step", initialStep);
        return newParams;
      });
    }
  }, [session?.user?.id, searchParams, setSearchParams]); // Only re-run if user ID changes

  if (loading) return <p>Loading...</p>;
  return (
    <BaseLayoutNoMenu>
      <div className="w-full max-w-screen-xl mx-auto">
        <div className="px-8">
          <WizardProgress
            userType={userType}
            step={searchParams.get("step") || ""}
          />
          {searchParams.get("step") === SETUP_KEYS.ACCOUNT && (
            <StepSelectType
              types={userAccountTypes}
              step="account"
              onSuccess={(data: any) => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  setUserType(get(data, "system_user_type") as UserType);
                  const next = nextStepByType(
                    get(
                      data,
                      "system_user_type",
                      SYSTEM_USER_TYPES.INVESTOR,
                    ) as UserType,
                    searchParams.get("step") || "",
                  );
                  newParams.set("step", next as string);
                  return newParams;
                });
              }}
            />
          )}
          {searchParams.get("step") === SETUP_KEYS.CLAIM && (
            <StepClaimOrganization
              step="preferences"
              onSuccess={() => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev); // ✅ clone, don't mutate
                  newParams.set("step", SETUP_KEYS.CONFIRMATION);
                  return newParams;
                });
              }}
            />
          )}
          {searchParams.get("step") === SETUP_KEYS.PREFERENCES && (
            <StepPreferences
              step={SETUP_KEYS.PREFERENCES}
              onSuccess={() => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev); // ✅ clone, don't mutate
                  newParams.set("step", SETUP_KEYS.CONFIRMATION);
                  return newParams;
                });
              }}
            />
          )}
          {searchParams.get("step") === SETUP_KEYS.CONFIRMATION && (
            <StepConfirmation step={SETUP_KEYS.CONFIRMATION} />
          )}
        </div>
      </div>
    </BaseLayoutNoMenu>
  );
};
